/**
 * useResponseMode
 *
 * Manages AI response mode selection (normal, quick, deep).
 *
 * Responsibilities:
 * - Track current response mode state
 * - Provide mode switching functionality
 * - Generate mode suffix for API calls
 * - Export RESPONSE_MODES configuration
 *
 * Interface:
 * @param {string} [initialMode='normal'] - Initial response mode
 * @returns {Object} { responseMode, setResponseMode, getModeSuffix, RESPONSE_MODES }
 */

import { useState, useCallback, useMemo } from 'react';
import { Laugh, Rabbit, Target } from 'lucide-react';

/**
 * Response mode configurations
 * - normal: Standard responses
 * - quick: Brief, direct responses
 * - deep: Detailed, in-depth analysis
 */
export const RESPONSE_MODES = {
  normal: {
    icon: Laugh,
    suffix: ""
  },
  quick: {
    icon: Rabbit,
    suffix: "[respond quickly, be briefly and direct. No extra chit chat.]"
  },
  deep: {
    icon: Target,
    suffix: "[provide detailed indepth research analysis, dont worry about tokens, were going to take our time.]"
  }
};

/**
 * Hook for managing response mode
 */
export function useResponseMode(initialMode = 'normal') {
  const [responseMode, setResponseMode] = useState(initialMode);

  /**
   * Get the suffix string for the current mode to append to API calls
   */
  const getModeSuffix = useCallback(() => {
    return RESPONSE_MODES[responseMode]?.suffix || '';
  }, [responseMode]);

  // Return stable object structure
  return useMemo(() => ({
    responseMode,
    setResponseMode,
    getModeSuffix,
    RESPONSE_MODES
  }), [responseMode, getModeSuffix]);
}
